/**
 * Remotion Prototype Demo
 *
 * This demo shows the Remotion-based carousel editor in action.
 * Run this to test the prototype before integrating into the main app.
 *
 * To use:
 * 1. Install remotion: npm install remotion @remotion/player @remotion/cli
 * 2. Import this component into a page
 * 3. Test drag, resize, select, and property editing
 */

import React from 'react';
import { RemotionCarouselEditor } from './editor/RemotionCarouselEditor';
import { OverlayData } from './types';

// Sample overlays to start with
const SAMPLE_OVERLAYS: OverlayData[] = [
  {
    id: 'text-1',
    type: 'text',
    text: '5 Tips for Better\nProductivity',
    position: { x: 10, y: 15 },
    width: 80,
    zIndex: 1,
    style: {
      fontFamily: 'Inter',
      fontSize: 56,
      color: '#ffffff',
      backgroundColor: '#000000',
      opacity: 85,
      textAlign: 'center',
      fontWeight: 'bold',
      fontStyle: 'normal',
      lineHeight: 1.3,
      borderRadius: 12,
      padding: { top: 24, right: 32, bottom: 24, left: 32 },
    },
  },
  {
    id: 'text-2',
    type: 'text',
    text: 'Swipe to learn more',
    position: { x: 25, y: 85 },
    width: 50,
    zIndex: 2,
    style: {
      fontFamily: 'Inter',
      fontSize: 24,
      color: '#ffffff',
      backgroundColor: '#2196F3',
      opacity: 100,
      textAlign: 'center',
      fontWeight: 'normal',
      fontStyle: 'normal',
      lineHeight: 1.4,
      borderRadius: 20,
      padding: { top: 12, right: 24, bottom: 12, left: 24 },
    },
  },
];

export const Demo: React.FC = () => {
  const handleOverlaysChange = (overlays: OverlayData[]) => {
    console.log('Overlays updated:', overlays);
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: '#0f0f0f',
        color: '#ffffff',
        fontFamily: 'Inter, system-ui, sans-serif',
      }}
    >
      <header style={{ padding: 24, borderBottom: '1px solid #333' }}>
        <h1 style={{ margin: 0 }}>Remotion Carousel Editor Prototype</h1>
        <p style={{ color: '#888', marginTop: 8 }}>
          Drag overlays to move, use handles to resize, click to select
        </p>
      </header>

      <main>
        <RemotionCarouselEditor
          initialOverlays={SAMPLE_OVERLAYS}
          backgroundColor="#1a1a2e"
          onOverlaysChange={handleOverlaysChange}
        />
      </main>

      <footer style={{ padding: 24, borderTop: '1px solid #333', color: '#666' }}>
        <h3>How it works:</h3>
        <ul>
          <li>
            <strong>Player:</strong> Remotion's Player renders the composition (same as export)
          </li>
          <li>
            <strong>Editing Layer:</strong> Transparent div on top handles drag/resize/select
          </li>
          <li>
            <strong>Properties Panel:</strong> Updates overlay data, which re-renders the Player
          </li>
          <li>
            <strong>Export:</strong> Use <code>renderStill()</code> with the same composition for
            pixel-perfect export
          </li>
        </ul>

        <h3 style={{ marginTop: 24 }}>Keyboard shortcuts:</h3>
        <ul>
          <li>
            <kbd>Delete</kbd> / <kbd>Backspace</kbd> - Delete selected overlay
          </li>
          <li>
            <kbd>Arrow keys</kbd> - Move selected overlay (hold Shift for 10px)
          </li>
          <li>
            <kbd>Escape</kbd> - Deselect
          </li>
        </ul>
      </footer>
    </div>
  );
};

export default Demo;
